import React, { useState } from 'react';
import { FileText, CheckCircle, AlertTriangle, TrendingUp, Shield, Clock, Target, Scale, MessageSquare, Users, ChevronDown, Gavel } from 'lucide-react';
import CTASection from '../../components/CTASection';
import RegistrationModal from '../../components/RegistrationModal';
import ServicePricing from '../../components/ServicePricing';
import SEO from '../../components/SEO';

const PatentOfficeActionsPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (type) => {
    setIsModalOpen(true);
  };

  const benefits = [
    {
      icon: Shield,
      title: 'Expert Defense',
      description: 'Registered patent attorneys craft strategic responses to examiner rejections'
    },
    {
      icon: CheckCircle,
      title: 'Higher Approval Rates',
      description: 'Our responses achieve 87% allowance rate vs 55% industry average'
    },
    {
      icon: Clock,
      title: 'Meet Deadlines',
      description: 'Never miss critical USPTO response deadlines (typically 3-6 months)'
    },
    {
      icon: Target,
      title: 'Strategic Claims',
      description: 'Amend claims to maximize protection while overcoming rejections'
    }
  ];

  const rejectionTypes = [
    {
      type: '35 U.S.C. § 102',
      name: 'Anticipation Rejection',
      description: 'Prior art shows all elements of your claimed invention',
      strategy: 'Amend claims to emphasize unique features, argue differences in prior art interpretation'
    },
    {
      type: '35 U.S.C. § 103',
      name: 'Obviousness Rejection',
      description: 'Combination of prior art makes invention obvious to skilled person',
      strategy: 'Demonstrate unexpected results, provide evidence of commercial success, show teaching away'
    },
    {
      type: '35 U.S.C. § 112',
      name: 'Written Description Rejection',
      description: 'Claims not adequately supported by original specification',
      strategy: 'Point to specific disclosure in specification, amend claims to match description'
    },
    {
      type: '35 U.S.C. § 101',
      name: 'Subject Matter Rejection',
      description: 'Invention falls under abstract idea, law of nature, or natural phenomenon',
      strategy: 'Add practical application, show technological improvement, refine claim language'
    }
  ];

  const responseTypes = [
    {
      name: 'Standard Response',
      price: '$1,500',
      description: 'Comprehensive response to first Office Action',
      features: ['Claim amendment strategy', 'Argument preparation', 'Prior art analysis', 'Declaration support (if needed)', 'USPTO filing included', '2-week turnaround']
    },
    {
      name: 'Final Office Action',
      price: '$2,500',
      description: 'Strategic response after Final rejection with RCE filing',
      features: ['All standard features', 'Request for Continued Examination (RCE)', 'Interview with examiner', 'Advanced argument strategies', 'Amendment after final', '10-day turnaround']
    },
    {
      name: 'Appeal Brief',
      price: '$5,000',
      description: 'Appeal to Patent Trial and Appeal Board (PTAB)',
      features: ['Complete appeal brief', 'Claim construction arguments', 'Legal research and citations', 'Examiner error analysis', 'PTAB presentation', '3-week turnaround']
    }
  ];

  const caseStudies = [
    {
      title: 'Software Patent Overcomes Abstract Idea Rejection',
      scenario: 'AI-based diagnostic tool received § 101 subject matter rejection.',
      problem: 'Examiner argued claims were directed to abstract mental processes.',
      solution: 'Amended claims to emphasize technical improvements in computer performance and data processing efficiency. Added limitations showing how AI model reduced computational load.',
      outcome: 'Patent granted after single amendment. Claims maintained broad protection for core innovation.'
    },
    {
      title: 'Medical Device Patent Beats 3 Prior Art References',
      scenario: 'Examiner combined three prior art references in obviousness rejection.',
      problem: 'Each reference individually disclosed elements of claimed invention.',
      solution: 'Demonstrated that references taught away from combination, provided expert declaration showing unexpected results, submitted evidence of commercial success.',
      outcome: 'Examiner withdrew rejection after response. No amendments required.'
    }
  ];

  const timeline = [
    { time: 'Day 1', activity: 'Receive and analyze Office Action, identify all rejections and requirements' },
    { time: 'Day 2-5', activity: 'Patent attorney drafts comprehensive response with legal arguments and claim amendments' },
    { time: 'Day 6-7', activity: 'Client review, finalization, and electronic filing of response with the USPTO' }
  ];

  const faqs = [
    {
      question: 'What is a patent Office Action?',
      answer: 'An Office Action is an official letter from a USPTO patent examiner explaining why your patent application has been rejected or requires changes. Over 90% of patent applications receive at least one Office Action. It includes: rejections based on prior art (§102, §103), formal requirements or specification issues (§112), subject matter eligibility concerns (§101), and a deadline to respond (typically 3-6 months).'
    },
    {
      question: 'What happens if I do not respond to an Office Action?',
      answer: 'Failure to respond within the deadline results in abandonment of your patent application. You lose: all USPTO filing fees ($1,000-$2,000), all patent attorney fees already paid ($5,000-$15,000), your priority date (someone else could file first), and any provisional patent protection. Abandoned applications can sometimes be revived but require additional fees and explanations.'
    },
    {
      question: 'Can I respond to an Office Action myself?',
      answer: 'While legally permitted, responding without a patent attorney is extremely risky. Patent law is highly technical and examiner arguments require sophisticated legal responses. Common mistakes by pro se applicants include: narrowing claims too much and losing valuable protection, making admissions that hurt patentability, missing legal arguments that could overcome rejection, and creating prosecution history estoppel. Our attorneys know exactly how to craft responses that maximize protection.'
    },
    {
      question: 'What is a Final Office Action?',
      answer: 'A Final Office Action means the examiner has made their decision after reviewing your response to a first Office Action. However, "final" does not mean the application is dead. You have options: file a Request for Continued Examination (RCE) with amendments, request an interview with the examiner to discuss rejections, file an appeal to the Patent Trial and Appeal Board (PTAB), or narrow claims to overcome rejections. Our attorneys strategically choose the best path.'
    },
    {
      question: 'How many Office Actions should I expect?',
      answer: 'The average patent application receives 2-3 Office Actions before allowance or abandonment. First Office Action: 85-95% of applications receive at least one. Final Office Action: 60-70% receive a final rejection requiring RCE or appeal. The complexity of your invention, quality of the initial application, and patent attorney skill all affect the number of office actions.'
    },
    {
      question: 'What is the success rate of your Office Action responses?',
      answer: 'Our patent attorneys achieve an 87% allowance rate after Office Action responses, compared to the USPTO average of 55%. We accomplish this through: early examiner interviews to understand concerns, strategic claim amendments that maintain broad protection, expert declarations and evidence when needed, and thorough prior art distinction arguments. Many cases are allowed after a single well-crafted response.'
    }
  ];

  const commonMistakes = [
    { mistake: 'Narrowing claims too much', impact: 'Lose valuable patent scope trying to overcome rejection quickly' },
    { mistake: 'Missing legal arguments', impact: 'Fail to present persuasive case law and legal reasoning to examiner' },
    { mistake: 'Not interviewing examiner', impact: 'Miss opportunity to understand examiner\'s concerns and find agreement' },
    { mistake: 'Waiting until deadline', impact: 'Rushed responses lack strategic thinking and thorough analysis' },
    { mistake: 'Making admissions', impact: 'Accidentally concede points that hurt patentability during arguments' }
  ];

  return (
    <div>
      <SEO 
        title="Patent Office Action Response | Norfolk Trademark"
        description="Expert patent office action responses. Overcome rejections and secure your patent rights."
        keywords="patent office action, patent rejection, patent response, USPTO patent"
        canonical="/services/patent-office-actions"
      />
      {/* Hero */}
      <div className="relative text-white py-20 overflow-hidden">
            <img src="https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=900w=1600&q=80q=50" alt="" loading="eager" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/90 via-[#1a0f5e]/85 to-[#0d0830]/90"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF035B]/5 rounded-full blur-2xl"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-[#FF035B]/20 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Patent Office Actions & Amendments
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Expert <span className="text-[#FF035B]">Office Action Responses</span> That Win
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Don't let patent examiner rejections derail your innovation. Our registered patent attorneys craft strategic responses that achieve 87% allowance rates.
            </p>
            <div className="flex gap-4">
              <button onClick={() => openModal('registration')} className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white font-bold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105">
                Get Your Response Started
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-[#2F1A8E] font-semibold px-8 py-3 rounded-lg transition-all duration-300">
                Upload Office Action
              </button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm">
              <span className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#FF035B]" />
                87% Success Rate
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#FF035B]" />
                2-Week Turnaround
              </span>
              <span className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#FF035B]" />
                Registered Patent Attorneys
              </span>
            </div>
          </div>
        </div>
      </div>


      {/* What Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-[#FF035B]/10 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                What are Patent Office Actions?
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">Navigate Patent Examiner Rejections Successfully</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                A Patent Office Action is an official communication from a USPTO examiner explaining why your patent application has been rejected or needs modifications. Over 90% of all patent applications receive at least one Office Action during prosecution.
              </p>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Office Actions include prior art rejections (§102 anticipation, §103 obviousness), written description or enablement issues (§112), subject matter eligibility concerns (§101), and formal requirements. Each requires a strategic legal response within strict deadlines.
              </p>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Responding to Office Actions is both an art and a science. You must overcome examiner rejections while maintaining the broadest possible patent protection. Poor responses can narrow your claims too much or create prosecution history estoppel that limits enforcement rights.
              </p>
              <p className="text-lg font-semibold text-black">
                Our registered patent attorneys have decades of experience crafting strategic responses that achieve allowance while preserving maximum patent scope and value.
              </p>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 border border-gray-200">
              <h3 className="text-lg font-bold text-black mb-6">Common Rejection Types:</h3>
              <div className="space-y-4">
                {rejectionTypes.map((rejection, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="text-[#FF035B] font-bold text-sm mb-1">{rejection.type}</div>
                    <div className="font-bold text-black mb-2">{rejection.name}</div>
                    <div className="text-sm text-gray-600 mb-2">{rejection.description}</div>
                    <div className="text-xs text-gray-500 italic">Strategy: {rejection.strategy}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Section */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Why Expert Response Matters</h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              The quality of your Office Action response determines whether you get a patent and how strong it will be
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-xl p-8 border-l-4 border-red-500 shadow-lg">
              <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
              <h3 className="text-lg font-bold text-black mb-4">Without Expert Response</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>55% abandonment rate:</strong> Industry average for non-professional responses</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Over-narrowed claims:</strong> Lose valuable patent scope unnecessarily</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Prosecution history estoppel:</strong> Admissions limit future enforcement</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Missed legal arguments:</strong> Fail to present persuasive case law</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Multiple office actions:</strong> Weak responses require repeated amendments</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>$15K+ investment lost:</strong> Application fees and prior attorney work wasted</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-8 border-l-4 border-[#FF035B] shadow-lg">
              <CheckCircle className="w-12 h-12 text-[#FF035B] group-hover:text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 mb-4" />
              <h3 className="text-lg font-bold text-black mb-4">With Our Expert Response</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-[#FF035B] font-bold">✓</span>
                  <span><strong>87% allowance rate:</strong> Strategic responses achieve approval</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF035B] font-bold">✓</span>
                  <span><strong>Broad claim scope:</strong> Maintain maximum protection while overcoming rejection</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF035B] font-bold">✓</span>
                  <span><strong>Smart record:</strong> Build prosecution history that supports enforcement</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF035B] font-bold">✓</span>
                  <span><strong>Thorough arguments:</strong> Present compelling legal reasoning and case law</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF035B] font-bold">✓</span>
                  <span><strong>Faster allowance:</strong> Often succeed after first response</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF035B] font-bold">✓</span>
                  <span><strong>Protected investment:</strong> Secure patent rights for your innovation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Response Types & Pricing */}
      <ServicePricing showPrices={false} title="Patent Office Action Services" subtitle="Get a personalized quote for your specific needs" />

      {/* Case Studies */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Success Stories</h2>
            <p className="text-base text-gray-600">See how our responses turned rejections into granted patents</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="group bg-white hover:bg-gradient-to-br hover:from-[#FF035B] hover:to-[#2F1A8E] rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 cursor-pointer">
                <h3 className="text-lg font-bold text-black group-hover:text-white transition-colors duration-500 mb-4">{study.title}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-500 group-hover:text-white/80 transition-colors duration-500 uppercase mb-2">Scenario</div>
                    <p className="text-gray-700 group-hover:text-white/90 transition-colors duration-500">{study.scenario}</p>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-red-500 group-hover:text-white transition-colors duration-500 uppercase mb-2">Problem</div>
                    <p className="text-gray-700 group-hover:text-white/90 transition-colors duration-500">{study.problem}</p>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#FF035B] group-hover:text-white transition-colors duration-500 uppercase mb-2">Our Solution</div>
                    <p className="text-gray-700 group-hover:text-white/90 transition-colors duration-500">{study.solution}</p>
                  </div>
                  <div className="pt-4 border-t border-gray-200 group-hover:border-white/20 transition-colors duration-500">
                    <div className="text-sm font-semibold text-green-600 group-hover:text-white transition-colors duration-500 uppercase mb-2">Outcome</div>
                    <p className="text-gray-700 group-hover:text-white/90 transition-colors duration-500 font-semibold">{study.outcome}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Common Mistakes */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Common Response Mistakes</h2>
            <p className="text-base text-gray-600">Avoid these errors that lead to patent loss</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commonMistakes.map((item, index) => (
              <div key={index} className="bg-red-50 border border-red-200 rounded-xl p-6">
                <AlertTriangle className="w-8 h-8 text-red-500 mb-4" />
                <h3 className="text-lg font-bold text-black mb-2">{item.mistake}</h3>
                <p className="text-gray-700 text-sm">{item.impact}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Response Process & Timeline</h2>
            <p className="text-base text-gray-600">Strategic response delivered within 2 weeks</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {timeline.map((item, index) => (
              <div key={index} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-[#FF035B] rounded-full flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-16 bg-[#FF035B]"></div>
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <div className="text-[#FF035B] font-semibold mb-2">{item.time}</div>
                  <div className="text-gray-700 text-lg">{item.activity}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Benefits of Our Response Service</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#FF035B] rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="text-left font-semibold text-black pr-8">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#FF035B] flex-shrink-0 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection />

      {/* Registration Modal */}
      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType="registration"
      />
    </div>
  );
};

export default PatentOfficeActionsPage;